import type { ResourceActionConfig } from "../types"
import { actionKeys, type ActionKey } from "../actions"

/**
 * Irish (Gaeilge) action translations
 */
export const actionTranslations: Record<ActionKey, ResourceActionConfig> = {
  [actionKeys.BUY]: {
    heading: { text: "Buy", translation: "Ceannaigh" },
    cta: { text: "Buy it", translation: "Ceannaigh é" },
    activityVerb: { text: "started reading", translation: "thosaigh ag léamh" },
    pastVerb: { text: "bought", translation: "cheannaigh" },
  },
  [actionKeys.DOWNLOAD]: {
    heading: { text: "Download", translation: "Íoslódáil" },
    cta: { text: "Download it", translation: "Íoslódáil é" },
    activityVerb: { text: "started using", translation: "thosaigh ag úsáid" },
    pastVerb: { text: "downloaded", translation: "d'íoslódáil" },
  },
  [actionKeys.LISTEN]: {
    heading: { text: "Listen", translation: "Éist" },
    cta: { text: "Listen now", translation: "Éist anois" },
    activityVerb: {
      text: "started listening to",
      translation: "thosaigh ag éisteacht le",
    },
    pastVerb: { text: "listened to", translation: "d'éist le" },
  },
  [actionKeys.WATCH]: {
    heading: { text: "Watch", translation: "Féach" },
    cta: { text: "Watch it", translation: "Féach air" },
    activityVerb: {
      text: "started watching",
      translation: "thosaigh ag féachaint ar",
    },
    pastVerb: { text: "watched", translation: "d'fhéach ar" },
  },
  [actionKeys.PLAY]: {
    heading: { text: "Play", translation: "Imir" },
    cta: { text: "Play it", translation: "Imir é" },
    activityVerb: { text: "started playing", translation: "thosaigh ag imirt" },
    pastVerb: { text: "played", translation: "d'imir" },
  },
  [actionKeys.ENROLL]: {
    heading: { text: "Enroll", translation: "Cláraigh" },
    cta: { text: "Enroll now", translation: "Cláraigh anois" },
    activityVerb: { text: "enrolled in", translation: "chláraigh i" },
    pastVerb: { text: "completed", translation: "chríochnaigh" },
  },
  [actionKeys.READ]: {
    heading: { text: "Read", translation: "Léigh" },
    cta: { text: "Read it", translation: "Léigh é" },
    activityVerb: { text: "started reading", translation: "thosaigh ag léamh" },
    pastVerb: { text: "read", translation: "léigh" },
  },
  [actionKeys.LEARN]: {
    heading: { text: "Learn", translation: "Foghlaim" },
    cta: { text: "Learn", translation: "Foghlaim" },
    activityVerb: {
      text: "started learning from",
      translation: "thosaigh ag foghlaim ó",
    },
    pastVerb: { text: "learned from", translation: "d'fhoghlaim ó" },
  },
  [actionKeys.LOOKUP]: {
    heading: { text: "Look Up", translation: "Féach suas" },
    cta: { text: "Look it up", translation: "Féach suas é" },
    activityVerb: { text: "started using", translation: "thosaigh ag úsáid" },
    pastVerb: { text: "used", translation: "d'úsáid" },
  },
  [actionKeys.USE]: {
    heading: { text: "Use", translation: "Úsáid" },
    cta: { text: "Use it", translation: "Úsáid é" },
    activityVerb: { text: "started using", translation: "thosaigh ag úsáid" },
    pastVerb: { text: "used", translation: "d'úsáid" },
  },
  [actionKeys.STUDY]: {
    heading: { text: "Study", translation: "Staidéar" },
    cta: { text: "Study now", translation: "Déan staidéar anois" },
    activityVerb: {
      text: "started studying",
      translation: "thosaigh ag staidéar",
    },
    pastVerb: { text: "studied", translation: "rinne staidéar ar" },
  },
  [actionKeys.JOIN]: {
    heading: { text: "Join", translation: "Bí páirteach" },
    cta: { text: "Join now", translation: "Bí páirteach anois" },
    activityVerb: { text: "joined", translation: "chuaigh isteach i" },
    pastVerb: { text: "joined", translation: "chuaigh isteach i" },
  },
  [actionKeys.ATTEND]: {
    heading: { text: "Attend", translation: "Freastail" },
    cta: { text: "Attend", translation: "Freastail air" },
    activityVerb: { text: "is attending", translation: "ag freastal ar" },
    pastVerb: { text: "attended", translation: "d'fhreastail ar" },
  },
  [actionKeys.CONTACT]: {
    heading: { text: "Contact", translation: "Déan teagmháil" },
    cta: { text: "Get in touch", translation: "Déan teagmháil" },
    activityVerb: {
      text: "started learning with",
      translation: "thosaigh ag foghlaim le",
    },
    pastVerb: { text: "learned with", translation: "d'fhoghlaim le" },
  },
  [actionKeys.LINKS]: {
    heading: { text: "Links", translation: "Naisc" },
    cta: { text: "Learn more", translation: "Foghlaim níos mó" },
    activityVerb: { text: "started using", translation: "thosaigh ag úsáid" },
    pastVerb: { text: "used", translation: "d'úsáid" },
  },
  // Sign the guestbook / an Ogham "stone". Irish drafted for native review: "sínigh" = sign (imperative),
  // "shínigh" = past tense (séimhiú).
  [actionKeys.SIGN]: {
    heading: { text: "Sign", translation: "Sínigh" },
    cta: { text: "Sign it", translation: "Sínigh é" },
    activityVerb: { text: "signed", translation: "shínigh" },
    pastVerb: { text: "signed", translation: "shínigh" },
  },
  // General "write" action (posts, entries, contributions) — kept distinct from SIGN so guestbooks read
  // "Sign" while other cases can read "Write". Irish drafted for native review: "scríobh" = write.
  [actionKeys.WRITE]: {
    heading: { text: "Write", translation: "Scríobh" },
    cta: { text: "Write it", translation: "Scríobh é" },
    activityVerb: { text: "started writing", translation: "thosaigh ag scríobh" },
    pastVerb: { text: "wrote", translation: "scríobh" },
  },
}

export default actionTranslations
